import { FC, useEffect } from "react";
import { useVideoConnectionOptions } from "../../hooks/useVideoConnectionOptions/useVideoConnectionOptions";
import { useLogContext } from "../../hooks/useLogContext/useLogContext";
import { MediaPlayer } from "../MediaPlayer/MediaPlayer";
import { VideoPlaybackProvider } from "../VideoPlaybackProvider/VideoPlaybackProvider";
import { VideoProvider } from "../VideoProvider";
import { JoinType } from "../../types/playback-provider";
import { ConferenceProvider } from "../ConferenceProvider/ConferenceProvider";
import { ConferencePlaybackProvider } from "../ConferencePlaybackProvider/ConferencePlaybackProvider";
import { VideoGlobalAPI } from "../VideoGlobalAPI/VideoGlobalAPI";
import { ConferenceGlobalAPI } from "../ConferenceGlobalAPI/ConferenceGlobalAPI";

export const MediaPlayerContainer: FC = () => {
	const { rum, connect, providerReady } = useLogContext();

	useEffect(() => {
		if (!rum) {
			connect();
		}

		rum?.recordPageView(window.location.pathname);
	}, [connect, window.location, rum, providerReady]);

	const onError = (error: any) => {
		console.error("[CLIENT:VideoProviderError] ", error);
		rum?.recordError(error);
	};

	const params = new URLSearchParams(window.location.search);
	const joinType = params.get("joinType") as JoinType | null;
	const conferenceName = params.get("conferenceName") || undefined;

	console.log(`MediaPlayerContainer: joinType: ${joinType}`);

	/**
	 * Note: The playback bot was originally built to support connecting to a
	 * Twilio Video Room only.
	 *
	 * We now support both Twilio Video, and also connecting to a Twilio Conference
	 * so as to handle conferences that are audio-room-only.
	 *
	 * We do this by passing in a `joinType` query param to the web app which is either
	 * "conference" or "video-room", and conditionally render the rest of the webapp from
	 * this point onwards based on that value.
	 *
	 * If the value is specified as "conference" we do it the new way.
	 * If the value is specified as anything else or just omitted (to support backwards
	 * compatability), we render it the old way (i.e. Video Room).
	 */

	if (joinType === "conference") {
		console.log("MediaPlayerContainer: Preparing app to connect to a Twilio Conference");

		if (!conferenceName) {
			throw new Error("Conference name is not specified");
		}

		return (
			<ConferenceProvider onError={onError} options={{ conferenceName }}>
				<ConferencePlaybackProvider>
					<ConferenceGlobalAPI />
					<MediaPlayer />
				</ConferencePlaybackProvider>
			</ConferenceProvider>
		);
	} else {
		console.log("MediaPlayerContainer: Preparing app to connect to a Twilio Video Room");

		const options = useVideoConnectionOptions();

		return (
			<VideoProvider options={options} onError={onError}>
				<VideoPlaybackProvider>
					<VideoGlobalAPI />
					<MediaPlayer />
				</VideoPlaybackProvider>
			</VideoProvider>
		);
	}
};
